import { errorNotification } from "../../Generic/Notifications";
import TaskService from "../services";
import {
	PAGE_SIZE,
	TASK_LIST_TOP_SENTINEL_ID,
	clearPending,
	isTaskListAtTop,
	pollAfterCursor,
	sameTaskIdSequence,
	scrollToRequiredItem,
	splitPollPage,
} from "./taskList.helpers";

let olderPageFetchInFlight = false;
let headPageFetchInFlight = false;
let olderPageFetchAbortController = null;

function abortOlderPageFetch() {
	if (!olderPageFetchAbortController) return;
	olderPageFetchAbortController.abort();
	olderPageFetchAbortController = null;
}

export function resetTaskListFetchLocks() {
	abortOlderPageFetch();
	olderPageFetchInFlight = false;
	headPageFetchInFlight = false;
}

export async function fetchFirstPage({ kitchenId, hasRows, setTaskList, setLoading, setPendingNewer, setPendingIsGap, setPendingStartCursor }) {
	if (headPageFetchInFlight) return;
	abortOlderPageFetch();
	headPageFetchInFlight = true;

	if (!hasRows) setLoading(true);

	try {
		const { data, error } = await TaskService.getRecentTasks(kitchenId, { size: PAGE_SIZE });
		if (data) {
			const metadata = data.metadata || {};
			setTaskList({
				mode: "replace",
				tasks: data.tasks || [],
				startCursor: metadata.startCursor,
				endCursor: metadata.endCursor,
				hasMoreOlder: Boolean(metadata.hasMore),
			});
			clearPending(setPendingNewer, setPendingIsGap, setPendingStartCursor);
		}
		if (error) {
			errorNotification(error);
			console.log(error);
		}
	} finally {
		setLoading(false);
		headPageFetchInFlight = false;
	}
}

export async function fetchOlder({ kitchenId, endCursor, hasMoreOlder, setTaskList, setLoadingOlder }) {
	if (olderPageFetchInFlight || !hasMoreOlder || !endCursor) return;
	olderPageFetchInFlight = true;
	olderPageFetchAbortController = new AbortController();
	const abortController = olderPageFetchAbortController;
	setLoadingOlder(true);
	try {
		const { data, error, aborted } = await TaskService.getRecentTasks(kitchenId, {
			size: PAGE_SIZE,
			before: endCursor,
			signal: abortController.signal,
		});
		if (aborted) return;
		if (data) {
			const metadata = data.metadata || {};
			setTaskList({
				mode: "append",
				tasks: data.tasks || [],
				endCursor: metadata.endCursor || endCursor,
				hasMoreOlder: Boolean(metadata.hasMore),
			});
		}
		if (error) {
			errorNotification("Could not fetch older tasks");
			console.log(error);
		}
	} finally {
		setLoadingOlder(false);
		olderPageFetchInFlight = false;
		if (olderPageFetchAbortController === abortController) olderPageFetchAbortController = null;
	}
}

export async function fetchNewer({
	kitchenId,
	startCursor,
	taskList,
	pendingNewer,
	pendingIsGap,
	setTaskList,
	setPendingNewer,
	setPendingIsGap,
	setPendingStartCursor,
}) {
	if (!startCursor) return;
	if (headPageFetchInFlight) return;

	headPageFetchInFlight = true;

	try {
		const { after, inclusive } = pollAfterCursor(taskList, startCursor);
		const rewound = inclusive === true;

		const { data, error } = await TaskService.getRecentTasks(kitchenId, {
			size: PAGE_SIZE,
			after,
		});
		if (error) {
			errorNotification("Could not fetch new tasks", { toastId: "fetch-newer-error" });
			console.error(error);
			return;
		}

		const { tasks, metadata } = data || {};
		if (!tasks?.length) {
			if (pendingNewer.length || pendingIsGap) {
				clearPending(setPendingNewer, setPendingIsGap, setPendingStartCursor);
			}
			return;
		}

		const { overlapping, notOnList, fresh } = splitPollPage(tasks, taskList, pendingNewer);
		const isGap = rewound ? fresh.length === PAGE_SIZE : metadata?.hasMore === true;

		if (isGap && !rewound) {
			// Same as today: ignore payload when the head hasMore gap fires.
			if (!pendingIsGap) {
				setPendingNewer([]);
				setPendingIsGap(true);
				setPendingStartCursor(null);
			}
			return;
		}

		if (overlapping.length) {
			setTaskList({ mode: "patch", tasks: overlapping });
		}

		if (isGap) {
			if (!pendingIsGap) {
				setPendingNewer([]);
				setPendingIsGap(true);
				setPendingStartCursor(null);
			}
			return;
		}

		if (!notOnList.length) {
			return;
		}

		/* Chip already showing the same newer page: skip setPendingNewer so we don't remount
		 * the chip every poll with a new array of identical ids. */
		if (sameTaskIdSequence(pendingNewer, notOnList)) {
			return;
		}

		if (isTaskListAtTop()) {
			setTaskList({
				mode: "prepend",
				tasks: notOnList,
				startCursor: metadata?.startCursor || startCursor,
			});
			clearPending(setPendingNewer, setPendingIsGap, setPendingStartCursor);
		} else {
			setPendingNewer(notOnList);
			setPendingIsGap(false);
			setPendingStartCursor(metadata?.startCursor);
		}
	} finally {
		headPageFetchInFlight = false;
	}
}

export async function applyPendingNewer({ pendingIsGap, pendingNewer, pendingStartCursor, kitchenId, hasRows, setTaskList, setLoading, setPendingNewer, setPendingIsGap, setPendingStartCursor, setChipBusy, }) {
	setChipBusy(true);
	try {
		if (pendingIsGap) {
			await fetchFirstPage({
				kitchenId,
				hasRows,
				setTaskList,
				setLoading,
				setPendingNewer,
				setPendingIsGap,
				setPendingStartCursor,
			});
		} else if (pendingNewer.length) {
			setTaskList({
				mode: "prepend",
				tasks: pendingNewer,
				startCursor: pendingStartCursor,
			});
			clearPending(setPendingNewer, setPendingIsGap, setPendingStartCursor);
		}
		scrollToRequiredItem(TASK_LIST_TOP_SENTINEL_ID);
	} finally {
		setChipBusy(false);
	}
}
